import { useState } from 'react'
import { motion } from 'framer-motion'
import { Plus, Edit2, Trash2, Eye, EyeOff, Calendar, Users, MapPin, Download } from 'lucide-react'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { events as defaultEvents } from '../../data/seedData'
import { formatDate, isFutureDate } from '../../utils/helpers'
import { Button, Modal, Input, Textarea, Select, Card, Badge, Checkbox } from '../../components/common'

const categories = [
  { value: 'worship', label: 'Worship' },
  { value: 'special', label: 'Special' },
  { value: 'youth', label: 'Youth' },
  { value: 'prayer', label: 'Prayer' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'general', label: 'General' },
]

const EventForm = ({ event, onSave, onClose }) => {
  const [formData, setFormData] = useState(event || {
    title: '',
    description: '',
    date: '',
    time: '',
    endTime: '',
    location: '',
    address: '',
    imageUrl: '',
    category: 'general',
    capacity: '',
    requiresRSVP: false,
    isPublished: true,
  })
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useFileUpload()

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const result = await upload(file, `events/${Date.now()}_${file.name}`)
    if (result.success) {
      setFormData({ ...formData, imageUrl: result.url })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave({
      ...formData,
      capacity: formData.capacity ? parseInt(formData.capacity) : null,
    })
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Event Title"
        required
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="Sunday Worship Service"
      />

      <Textarea
        label="Description"
        rows={3}
        required
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="Describe the event..."
      />

      <div className="grid md:grid-cols-3 gap-4">
        <Input
          label="Date"
          type="date"
          required
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
        />
        <Input
          label="Start Time"
          type="time"
          required
          value={formData.time?.replace(/\s*(AM|PM)/i, '')}
          onChange={(e) => setFormData({ ...formData, time: e.target.value })}
        />
        <Input
          label="End Time (optional)"
          type="time"
          value={formData.endTime?.replace(/\s*(AM|PM)/i, '')}
          onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Location"
          required
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="Main Sanctuary"
        />
        <Input
          label="Full Address (optional)"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="123 Faith Avenue, Clarksville, TN"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Select
          label="Category"
          required
          options={categories}
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
        />
        <Input
          label="Capacity (optional)"
          type="number"
          value={formData.capacity}
          onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
          placeholder="Leave blank for unlimited"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Image
        </label>
        <div className="flex items-center gap-4">
          {formData.imageUrl && (
            <img
              src={formData.imageUrl}
              alt="Preview"
              className="w-24 h-16 object-cover rounded-lg"
            />
          )}
          <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <span className="text-sm">{uploading ? 'Uploading...' : 'Upload Image'}</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <Checkbox
          label="Requires RSVP"
          checked={formData.requiresRSVP}
          onChange={(e) => setFormData({ ...formData, requiresRSVP: e.target.checked })}
        />
        <Checkbox
          label="Published"
          checked={formData.isPublished}
          onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {event ? 'Update Event' : 'Create Event'}
        </Button>
      </div>
    </form>
  )
}

const EventsManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [filter, setFilter] = useState('upcoming')

  const { data: events, add, update, remove } = useCollection('events', {
    orderByField: 'date',
    orderDirection: 'asc',
  })

  const displayEvents = events?.length > 0 ? events : defaultEvents

  const filteredEvents = displayEvents.filter(event => {
    if (filter === 'upcoming') return isFutureDate(event.date)
    if (filter === 'past') return !isFutureDate(event.date)
    return true
  })

  const handleSave = async (data) => {
    if (editingEvent?.id) {
      await update(editingEvent.id, data)
    } else {
      await add(data)
    }
    setShowModal(false)
    setEditingEvent(null)
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await remove(deleteConfirm)
      setDeleteConfirm(null)
    }
  }

  const togglePublished = async (event) => {
    await update(event.id, { isPublished: !event.isPublished })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Events</h1>
          <p className="text-gray-500">Manage church events and registrations</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingEvent(null)
            setShowModal(true)
          }}
        >
          Add Event
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['upcoming', 'past', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
              filter === f
                ? 'bg-primary-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Events List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Event</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Date & Time</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Location</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredEvents.map((event) => (
              <tr key={event.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {event.imageUrl ? (
                      <img
                        src={event.imageUrl}
                        alt=""
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-primary-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-dark-100">{event.title}</p>
                      <Badge variant="gray" size="sm">{event.category}</Badge>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-dark-100">{formatDate(event.date, 'MMM d, yyyy')}</p>
                  <p className="text-sm text-gray-500">{event.time}</p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <Badge
                      variant={event.isPublished ? 'success' : 'gray'}
                      size="sm"
                    >
                      {event.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                    {event.requiresRSVP && (
                      <Badge variant="info" size="sm" icon={Users}>
                        RSVP
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => togglePublished(event)}
                      className={`p-2 rounded-lg transition-colors ${
                        event.isPublished
                          ? 'text-emerald-600 hover:bg-emerald-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      {event.isPublished ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingEvent(event)
                        setShowModal(true)
                      }}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(event.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredEvents.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No {filter} events</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingEvent(null)
        }}
        title={editingEvent ? 'Edit Event' : 'Create Event'}
        size="lg"
      >
        <EventForm
          event={editingEvent}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingEvent(null)
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Event"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this event? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default EventsManager
